/**
 * Ícones SVG para marcadores de poste no mapa FTTH.
 * Dois tipos: com transformador (com_trafo) e sem transformador (sem_trafo).
 * A cor do poste (corpo do poste) é definida pelo parâmetro color.
 */

export const POSTE_ICON_OPTIONS = [
  { id: 'sem_trafo', label: 'Sem transformador' },
  { id: 'com_trafo', label: 'Com transformador' },
] as const;

export type PosteIconId = (typeof POSTE_ICON_OPTIONS)[number]['id'];

const DEFAULT_POSTE_COLOR = '#666666';

/** Placeholder substituído pela cor do poste (corpo do poste) */
const POLE_COLOR_PLACEHOLDER = '{{POLE_COLOR}}';
/** Cor mais escura para sombra do poste */
const POLE_SHADOW_PLACEHOLDER = '{{POLE_SHADOW}}';

const SVG_COM_TRAFO = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width="100%" height="100%">
  <rect x="173" y="0" width="42" height="400" fill="${POLE_COLOR_PLACEHOLDER}" />  

  <rect x="100" y="30" width="185" height="30" fill="${POLE_COLOR_PLACEHOLDER}" />  
  
  <rect x="60" y="85" width="265" height="30" fill="${POLE_COLOR_PLACEHOLDER}" />  

  <line x1="176" y1="85" x2="150" y2="59" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />
  <line x1="215" y1="85" x2="240" y2="59" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />
  <line x1="175" y1="150" x2="130" y2="114" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />
  <line x1="215" y1="150" x2="260" y2="114" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />

  <path d="M 110 30 L 112 15 L 123 15 L 125 30 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  <path d="M 260 30 L 262 15 L 273 15 L 275 30 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  <path d="M 70 85 L 72 70 L 83 70 L 85 85 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  <path d="M 300 85 L 302 70 L 313 70 L 315 85 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>

  <rect x="130" y="150" width="45" height="75" rx="3" fill="${POLE_COLOR_PLACEHOLDER}" />
  <rect x="130" y="150" width="20" height="75" rx="3" fill="${POLE_COLOR_PLACEHOLDER}" /> 
  <rect x="121" y="160" width="10" height="55" fill="${POLE_COLOR_PLACEHOLDER}" /> 
  <rect x="115" y="165" width="10" height="45" fill="${POLE_COLOR_PLACEHOLDER}" /> 
  
  <path d="M 155 150 Q 175 100 190 85" fill="none" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="1.5"/>
  <path d="M 165 150 Q 185 110 200 85" fill="none" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="1.5"/>
</svg>`;

const SVG_SEM_TRAFO = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" width="100%" height="100%">
  <rect x="173" y="0" width="42" height="400" fill="${POLE_COLOR_PLACEHOLDER}" />
  
  <rect x="100" y="30" width="185" height="30" fill="${POLE_COLOR_PLACEHOLDER}" />
  
  
  <rect x="60" y="85" width="265" height="30" fill="${POLE_COLOR_PLACEHOLDER}" />
  

  <line x1="176" y1="85" x2="150" y2="59" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />
  <line x1="215" y1="85" x2="240" y2="59" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />
  <line x1="175" y1="150" x2="130" y2="114" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />
  <line x1="215" y1="150" x2="260" y2="114" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="3" />

  <path d="M 110 30 L 112 15 L 123 15 L 125 30 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  <path d="M 260 30 L 262 15 L 273 15 L 275 30 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  <path d="M 70 85 L 72 70 L 83 70 L 85 85 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  <path d="M 300 85 L 302 70 L 313 70 L 315 85 Z" fill="${POLE_COLOR_PLACEHOLDER}"/>
  
  <path d="M 155 150 Q 175 100 190 85" fill="none" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="1.5"/>
  <path d="M 165 150 Q 185 110 200 85" fill="none" stroke="${POLE_COLOR_PLACEHOLDER}" stroke-width="1.5"/>
</svg>`;

function getPosteSvgByIconId(iconId: string | null | undefined): string {
  switch (iconId) {
    case 'com_trafo':
      return SVG_COM_TRAFO;
    case 'sem_trafo':
    default:
      return SVG_SEM_TRAFO;
  }
}

/** Garante cor hex válida; retorna cor mais escura para sombra. */
function parseColor(color: string | null | undefined): { main: string; shadow: string } {
  const c = color && /^#[0-9A-Fa-f]{3,8}$/.test(color.trim()) ? color.trim() : DEFAULT_POSTE_COLOR;
  const num = parseInt(c.slice(1), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) * 0.7);
  const g = Math.max(0, ((num >> 8) & 0xff) * 0.7);
  const b = Math.max(0, (num & 0xff) * 0.7);
  const shadow = '#' + [r, g, b].map(x => Math.round(x).toString(16).padStart(2, '0')).join('');
  return { main: c, shadow };
}

/**
 * Retorna data URL do ícone do poste para uso em marcadores (img src).
 * A cor do poste é definida por color (campo color do poste).
 */
export function getPosteIconDataUrl(iconId?: string | null, color?: string | null): string {
  const { main, shadow } = parseColor(color);
  let svg = getPosteSvgByIconId(iconId ?? 'sem_trafo');
  svg = svg.split(POLE_COLOR_PLACEHOLDER).join(main);
  svg = svg.split(POLE_SHADOW_PLACEHOLDER).join(shadow);
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}
